import { CheckOutlined, LockOutlined } from '@ant-design/icons'
import { Button, Popconfirm } from 'antd'
import React, { useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min'
import { formatCategoryTitle } from 'src/constants/planFeatureCategories'
import { planDisplayName } from 'src/constants/planDisplay'
import {
  cancelDowngrade,
  cancelSubscription,
} from 'src/features/SubscriptionSlice'
import { toggleSubscription } from 'src/features/VenueSlice'
import {
  RejectedNotification,
  SuccessNotification,
} from 'src/notifications/SuccessNotification'
import styled from 'styled-components/macro'

const PAID_PLAN_KEYS = ['pro', 'growth', 'starter']

const FREE_PLAN_CHOOSE_FOOTNOTE =
  'Need more recipes, invoice uploads, roles, or Ollie questions? Choose Starter, Growth, or Pro.'

const SubscriptionCard = ({
  plan,
  price,
  title,
  features,
  priceId,
  isSubscribed = false,
  isPopular = false,
}) => {
  const dispatch = useDispatch()
  const history = useHistory()
  const venue = useSelector(state => state.venue.venue)
  const venueId = venue?._id
  const account = useSelector(state => state.auth.account)
  const isLoading = useSelector(state => state.venue.isLoading)
  const [downgradeLoading, setDowngradeLoading] = useState(false)

  const isOmsPlatformAdmin = Boolean(account?.isAdmin)

  const { activeSubscription, activePaidPlanKey, isDowngradeScheduled } =
    useMemo(() => {
      const subscriptions = venue?.newPricing ?? {}
      const paidPlanKey = PAID_PLAN_KEYS.find(
        key => subscriptions[key]?.isSubscribed,
      )
      const subscription = paidPlanKey ? subscriptions[paidPlanKey] : null
      const scheduled = venue?.cancelSubscriptionScheduled

      return {
        activePaidPlanKey: paidPlanKey || null,
        activeSubscription: subscription,
        isDowngradeScheduled:
          Boolean(subscription?.subscriptionId) &&
          scheduled?.subscriptionId === subscription?.subscriptionId,
      }
    }, [venue?.newPricing, venue?.cancelSubscriptionScheduled])

  const activePlanLabel = activePaidPlanKey
    ? activePaidPlanKey.charAt(0).toUpperCase() + activePaidPlanKey.slice(1)
    : null

  const hasStripeSubscription = Boolean(activeSubscription?.subscriptionId)
  const hasPaidPlan = Boolean(activePaidPlanKey)

  const handleSwitchToFree = async () => {
    if (!venueId || !activePaidPlanKey) {
      return
    }

    setDowngradeLoading(true)
    try {
      if (hasStripeSubscription) {
        const response = await dispatch(
          cancelSubscription(activeSubscription.subscriptionId, venueId),
        )

        if (response?.success) {
          SuccessNotification({
            message: 'Downgrade scheduled',
            description:
              response?.message ||
              `Your downgrade to ${planDisplayName(
                'Free',
              )} is scheduled for the end of your billing period.`,
          })
          setTimeout(() => window.location.reload(), 1500)
        } else {
          RejectedNotification({
            message: 'Could not schedule downgrade',
            description:
              response?.error || 'Please try again or contact support.',
          })
        }
        return
      }

      await dispatch(
        toggleSubscription({
          venueId,
          plan: activePlanLabel,
          isSubscribed: true,
          isAdmin: isOmsPlatformAdmin,
        }),
      )
    } catch (error) {
      RejectedNotification({
        message: `Could not switch to ${planDisplayName('Free')}`,
        description: 'Please try again or contact support.',
      })
    } finally {
      setDowngradeLoading(false)
    }
  }

  const handleCancelScheduledDowngrade = async () => {
    const cancelId = venue?.cancelSubscriptionScheduled?._id
    if (!cancelId) {
      return
    }

    setDowngradeLoading(true)
    try {
      const response = await dispatch(cancelDowngrade(cancelId))

      if (response?.success) {
        SuccessNotification({
          message: 'Downgrade cancelled',
          description:
            response?.message || 'Your current plan will continue as normal.',
        })
        setTimeout(() => window.location.reload(), 1500)
      } else {
        RejectedNotification({
          message: 'Could not cancel downgrade',
          description:
            response?.error || 'Please try again or contact support.',
        })
      }
    } catch (error) {
      RejectedNotification({
        message: 'Could not cancel downgrade',
        description: 'Please try again or contact support.',
      })
    } finally {
      setDowngradeLoading(false)
    }
  }

  const renderFreeFooter = () => {
    if (isSubscribed) {
      return (
        <>
          <ChooseButton type='primary' disabled>
            You&apos;re on this plan
          </ChooseButton>
          <FreeFootnote>{FREE_PLAN_CHOOSE_FOOTNOTE}</FreeFootnote>
        </>
      )
    }

    if (isDowngradeScheduled) {
      return (
        <>
          <ChooseButton
            type='primary'
            loading={downgradeLoading}
            onClick={handleCancelScheduledDowngrade}
          >
            Cancel scheduled downgrade
          </ChooseButton>
          <FreeFootnote>
            Your switch to {planDisplayName('Free')} is scheduled for the end of
            your billing period.
          </FreeFootnote>
        </>
      )
    }

    if (hasPaidPlan) {
      const freeName = planDisplayName('Free')
      const confirmTitle = hasStripeSubscription
        ? `Switch to ${freeName}? You will keep full access until the end of your current billing period, then move to ${freeName} with no further charges.`
        : `Switch to ${freeName}? Your venue will move to ${freeName} straight away.`

      return (
        <>
          <Popconfirm
            title={confirmTitle}
            okText={`Switch to ${freeName}`}
            cancelText='Keep current plan'
            onConfirm={handleSwitchToFree}
          >
            <ChooseButton type='primary' loading={downgradeLoading}>
              Switch to {freeName}
            </ChooseButton>
          </Popconfirm>
          <FreeFootnote>
            {hasStripeSubscription
              ? 'Downgrades take effect at the end of your billing period — same as cancelling your subscription.'
              : `Admin-activated plans move to ${freeName} immediately when you switch.`}
          </FreeFootnote>
        </>
      )
    }

    return <FreeFootnote>{FREE_PLAN_CHOOSE_FOOTNOTE}</FreeFootnote>
  }

  return (
    <Card $isSubscribed={isSubscribed} $isPopular={isPopular}>
      <PriceBadge>£{price / 100} /month</PriceBadge>
      {isSubscribed && <CurrentPlanBadge>Current plan</CurrentPlanBadge>}
      {!isSubscribed && isPopular && <PopularBadge>Most popular</PopularBadge>}

      <CardHeader>
        <PlanType>{planDisplayName(plan)}</PlanType>
        <Title>{title}</Title>
      </CardHeader>

      <FeatureScroll>
        <FeatureList>
          {features?.map(feature => (
            <FeatureGroup key={feature.title}>
              <CategoryTitle>
                {formatCategoryTitle(feature.title)}
              </CategoryTitle>
              <ul>
                {feature.features.map((item, index) => {
                  const isObject = typeof item === 'object'
                  const text = isObject ? item.text : item
                  const limited = isObject ? Boolean(item.limited) : false

                  return (
                    <FeatureItem key={index}>
                      <FeatureIcon $limited={limited}>
                        {limited ? <LockOutlined /> : <CheckOutlined />}
                      </FeatureIcon>
                      <FeatureText $limited={limited}>{text}</FeatureText>
                    </FeatureItem>
                  )
                })}
              </ul>
            </FeatureGroup>
          ))}
        </FeatureList>
      </FeatureScroll>

      <CardFooter>
        {plan === 'Free' ? (
          renderFreeFooter()
        ) : (
          <>
            <ChooseButton
              type='primary'
              disabled={isSubscribed}
              onClick={() => {
                history.push(`/v/${venueId}/subscriptions/checkout`, {
                  priceId,
                  price,
                })
              }}
            >
              {isSubscribed ? "You're on this plan" : 'Choose this plan'}
            </ChooseButton>
            {isOmsPlatformAdmin && (
              <AdminButton
                type='primary'
                loading={isLoading}
                onClick={() => {
                  dispatch(
                    toggleSubscription({
                      venueId,
                      plan,
                      isSubscribed,
                      isAdmin: true,
                    }),
                  )
                }}
              >
                {isSubscribed ? 'Cancel subscription' : 'Admin activate'}
              </AdminButton>
            )}
            <CancelNote>Cancel anytime, no hidden fees</CancelNote>
          </>
        )}
      </CardFooter>
    </Card>
  )
}

const Card = styled.div`
  width: calc(25% - 20px);
  min-height: 100%;
  display: flex;
  flex-direction: column;
  background: ${({ theme }) => theme.colours.mayoWhite};
  border-radius: 16px;
  box-shadow: ${({ $isPopular }) =>
    $isPopular
      ? '0 10px 28px rgba(0, 0, 0, 0.12)'
      : '0 4px 10px rgba(0, 0, 0, 0.08)'};
  padding: 48px 18px 18px;
  text-align: center;
  position: relative;
  margin: 0 10px;
  border: 3px solid
    ${({ $isSubscribed, $isPopular, theme }) =>
      $isSubscribed || $isPopular ? theme.colours.lettuceGreen : 'transparent'};
  transform: ${({ $isPopular, $isSubscribed }) =>
    $isPopular && !$isSubscribed ? 'translateY(-6px)' : 'none'};
  transition: box-shadow 0.2s ease, border-color 0.2s ease, transform 0.2s ease;

  &:hover {
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.14);
    border-color: ${({ theme }) => theme.colours.lettuceGreen};
  }
`

const PriceBadge = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  background: ${({ theme }) => theme.colours.avocadoGreen};
  color: ${({ theme }) => theme.colours.mayoWhite};
  padding: 8px 14px;
  font-size: 18px;
  font-weight: 800;
  border-top-right-radius: 14px;
  border-bottom-left-radius: 14px;
`

const CurrentPlanBadge = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  background: ${({ theme }) => theme.colours.lettuceGreen};
  color: ${({ theme }) => theme.colours.bbqBlack};
  border: none;
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  border-top-left-radius: 14px;
  border-bottom-right-radius: 14px;
  z-index: 2;
`

const PopularBadge = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  background: ${({ theme }) => theme.colours.bbqBlack};
  color: ${({ theme }) => theme.colours.mayoWhite};
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  border-top-left-radius: 14px;
  border-bottom-right-radius: 14px;
  z-index: 2;
`

const CardHeader = styled.div`
  flex-shrink: 0;
  margin-bottom: 12px;
`

const Title = styled.h2`
  font-size: 18px;
  font-weight: 700;
  line-height: 1.35;
  margin: 8px 0 0;
  color: ${({ theme }) => theme.colours.bbqBlack};
`

const PlanType = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.colours.oliveGrey};
  font-size: 14px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
`

const FeatureScroll = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  margin-bottom: 4px;
`

const FeatureList = styled.div`
  text-align: left;
`

const FeatureGroup = styled.div`
  margin-bottom: 14px;

  ul {
    list-style: none;
    margin: 0;
    padding: 0;
  }
`

const CategoryTitle = styled.p`
  font-weight: 700;
  font-size: 13px;
  margin: 0 0 6px;
  color: ${({ theme }) => theme.colours.bbqBlack};
`

const FeatureItem = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 13px;
  line-height: 1.45;
  margin-bottom: 7px;
`

const FeatureIcon = styled.span`
  flex-shrink: 0;
  width: 18px;
  margin-top: 2px;
  color: ${({ $limited, theme }) =>
    $limited ? theme.colours.oliveGrey : theme.colours.lettuceGreen};
  font-size: ${({ $limited }) => ($limited ? '13px' : '14px')};
`

const FeatureText = styled.span`
  color: ${({ $limited, theme }) =>
    $limited ? theme.colours.oliveGrey : theme.colours.bbqBlack};
`

const CardFooter = styled.div`
  flex-shrink: 0;
  margin-top: auto;
  padding-top: 14px;
  border-top: 1px solid ${({ theme }) => theme.colours.panSilver};
`

const FreeFootnote = styled.p`
  margin: 10px 0 0;
  color: ${({ theme }) => theme.colours.oliveGrey};
  font-size: 13px;
  line-height: 1.5;
  text-align: center;
`

const ChooseButton = styled(Button)`
  && {
    width: 100%;
    margin-bottom: 8px;
    height: 44px;
    font-weight: 700;
    background: ${({ theme }) => theme.colours.lettuceGreen};
    border-color: ${({ theme }) => theme.colours.lettuceGreen};
    color: ${({ theme }) => theme.colours.bbqBlack};
  }

  &&:hover,
  &&:focus {
    background: ${({ theme }) => theme.colours.avocadoGreen};
    border-color: ${({ theme }) => theme.colours.avocadoGreen};
    color: ${({ theme }) => theme.colours.bbqBlack};
  }

  &&[disabled] {
    background: ${({ theme }) => theme.colours.panSilver};
    border-color: ${({ theme }) => theme.colours.panSilver};
    color: ${({ theme }) => theme.colours.oliveGrey};
  }
`

const AdminButton = styled(Button)`
  && {
    width: 100%;
    margin-bottom: 8px;
    height: 40px;
    font-weight: 600;
  }
`

const CancelNote = styled.p`
  margin: 4px 0 0;
  font-size: 13px;
  color: ${({ theme }) => theme.colours.oliveGrey};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;

  &::before {
    content: '✓';
    color: ${({ theme }) => theme.colours.lettuceGreen};
    font-weight: 700;
  }
`

export default SubscriptionCard
